import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Stack,
  Chip,
  Divider,
  Collapse
} from '@mui/material';
import {
  ErrorOutline,
  Refresh,
  BugReport,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  Home
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error using production-safe logger
    logger.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external service
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: globalThis.location.href
    };

    logger.error('Error logged:', errorData);
  };

  handleReload = () => {
    globalThis.location.reload();
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  handleCopyError = () => {
    const errorText = `
Greška u OmniPass aplikaciji:
${this.state.error?.message}

Stack Trace:
${this.state.error?.stack}

Component Stack:
${this.state.errorInfo?.componentStack}

Vreme: ${new Date().toLocaleString('sr-RS')}
URL: ${globalThis.location.href}
Browser: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorText);
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          p={3}
          sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              textAlign: 'center',
              borderRadius: 2
            }}
          >
            <ErrorOutline
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2
              }}
            />

            <Typography variant="h4" color="error" gutterBottom>
              Ups! Nešto je pošlo po zlu
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              Dogodila se neočekivana greška u aplikaciji. Naš tim je automatski obavešten
              i radi na rešavanju problema.
            </Typography>

            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                <strong>Greška:</strong> {this.state.error?.message}
              </Typography>
            </Alert>

            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} sx={{ mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleRetry}
                size="large"
              >
                Pokušaj Ponovo
              </Button>

              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                size="large"
              >
                Osveži Stranicu
              </Button>

              <ErrorBoundaryHomeButton />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Button
                startIcon={this.state.showDetails ? <ExpandLess /> : <ExpandMore />}
                onClick={() => this.setState((prevState) => ({ showDetails: !prevState.showDetails }))}
                size="small"
              >
                {this.state.showDetails ? 'Sakrij' : 'Prikaži'} Tehničke Detalje
              </Button>

              <Collapse in={this.state.showDetails}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Stack Trace:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: 200,
                          bgcolor: 'background.paper',
                          p: 1,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider'
                        }}
                      >
                        {this.state.error?.stack}
                      </Typography>
                    </Box>

                    {this.state.errorInfo?.componentStack && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Component Stack:
                        </Typography>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontSize: '0.75rem',
                            overflow: 'auto',
                            maxHeight: 150,
                            bgcolor: 'background.paper',
                            p: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider'
                          }}
                        >
                          {this.state.errorInfo.componentStack}
                        </Typography>
                      </Box>
                    )}

                    <Box display="flex" gap={1}>
                      <Chip
                        icon={<BugReport />}
                        label="Error ID"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={new Date().toLocaleString('sr-RS')}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    <Button
                      startIcon={<ContentCopy />}
                      onClick={this.handleCopyError}
                      size="small"
                      variant="outlined"
                    >
                      Kopiraj Detalje Greške
                    </Button>
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Separate component for Home button to use hooks
const ErrorBoundaryHomeButton: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <Button
      variant="outlined"
      startIcon={<Home />}
      onClick={handleGoHome}
      size="large"
    >
      Idi na Početnu
    </Button>
  );
};

export default ErrorBoundary;
